import React from 'react';
import styled from 'styled-components';
import { } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1509660933844-6910e12765a0?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

 

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #004744;
  font-style: italic;
`;

const SubTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #e0e0e0;
  text-align: center;
  font-style: italic;
  font-weight: normal;
`;

const BeforeAfterContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const BeforeAfterSet = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BeforeAfterItem = styled.div`
  width: 100%;
  height: 250px;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
`;

const IconContainer = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #004744;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #004744;
  font-style: italic;
`;

const ServiceDescription = styled.p`
  font-size: 1rem;
  color: #e0e0e0;
  line-height: 1.5;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Restyling: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const services = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="3" y1="9" x2="21" y2="9"></line>
          <line x1="9" y1="21" x2="9" y2="9"></line>
        </svg>
      ),
      title: language === 'it' ? 'Progettazione degli Spazi' : 'Space Planning',
      description: language === 'it' 
        ? 'Riprogettiamo gli spazi esistenti per ottimizzare la funzionalità e migliorare il flusso, creando ambienti più accoglienti per gli ospiti.'
        : 'We redesign existing spaces to optimize functionality and improve flow, creating more welcoming environments for guests.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
      ),
      title: language === 'it' ? 'Ristrutturazione' : 'Renovation',
      description: language === 'it' 
        ? 'Eseguiamo interventi di ristrutturazione mirati per aggiornare gli elementi datati e valorizzare gli aspetti positivi della proprietà.'
        : 'We carry out targeted renovation interventions to update dated elements and enhance the positive aspects of the property.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ),
      title: language === 'it' ? 'Gestione Progetto' : 'Project Management',
      description: language === 'it' 
        ? 'Coordiniamo tutti gli aspetti del restyling, dalla progettazione all\'esecuzione, garantendo rispetto dei tempi e del budget.'
        : 'We coordinate all aspects of restyling, from design to execution, ensuring respect of time and budget.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="2" y1="12" x2="22" y2="12"></line>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      ),
      title: language === 'it' ? 'Design Personalizzato' : 'Custom Design',
      description: language === 'it' 
        ? 'Creiamo soluzioni di design su misura che rispecchiano il carattere della proprietà e soddisfano le esigenze specifiche del mercato target.'
        : 'We create tailor-made design solutions that reflect the character of the property and meet the specific needs of the target market.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 3v4M3 5h4M6 17v4M4 19h4M13 3l4 4L3 21l4-4L13 3z"></path>
        </svg>
      ),
      title: language === 'it' ? 'Finiture e Materiali' : 'Finishes and Materials',
      description: language === 'it' 
        ? 'Selezioniamo finiture e materiali di qualità che sono sia esteticamente attraenti che durevoli, perfetti per l\'uso in proprietà in affitto.'
        : 'We select quality finishes and materials that are both aesthetically appealing and durable, perfect for use in rental properties.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      ),
      title: language === 'it' ? 'Consulenza Esperti' : 'Expert Consultation',
      description: language === 'it' 
        ? 'Offriamo consulenze con esperti del settore per guidarti attraverso le decisioni di restyling, assicurando il massimo ritorno sull\'investimento.'
        : 'We offer consultations with industry experts to guide you through restyling decisions, ensuring maximum return on investment.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Restyling | J-O-H' : 'Restyling | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizio di restyling professionale per rinnovare e valorizzare la tua proprietà, aumentandone l\'attrattiva per gli affitti brevi' 
            : 'Professional restyling service to renovate and enhance your property, increasing its attractiveness for short-term rentals'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <HeroContent>
          <PageTitle>RESTYLING</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Il Restyling è un servizio completo di rinnovamento degli spazi che trasforma la tua proprietà esistente in un\'attraente destinazione per gli affitti brevi. A differenza del Home Staging, che si concentra principalmente sull\'arredamento e sulla presentazione, il Restyling può includere interventi più sostanziali come ristrutturazioni, cambiamenti strutturali e aggiornamenti significativi.'
              : 'Restyling is a complete space renewal service that transforms your existing property into an attractive destination for short-term rentals. Unlike Home Staging, which focuses primarily on furnishing and presentation, Restyling can include more substantial interventions such as renovations, structural changes, and significant updates.'}
          </Description>
          <Description>
            {language === 'it' 
              ? 'Prepariamo la proprietà per l\'affitto, includendo la gestione di eventuali ristrutturazioni o riparazioni.'
              : 'We prepare the property for rent, including managing any renovations or repairs.'}
          </Description>
          
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={index}>
                <IconContainer>
                  {service.icon}
                </IconContainer>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceCard>
            ))}
          </ServicesGrid>
          
          
          
          <Description>
            {language === 'it' 
              ? 'Il nostro servizio di Restyling è progettato per massimizzare il potenziale di guadagno della tua proprietà attraverso affitti brevi. Grazie a una combinazione di rinnovo degli spazi, decorazione professionale e promozione online efficace, la proprietà diventa una destinazione ambita per gli ospiti in cerca di un\'esperienza di viaggio indimenticabile.'
              : 'Our Restyling service is designed to maximize the earning potential of your property through short-term rentals. Thanks to a combination of space renewal, professional decoration and effective online promotion, the property becomes a sought-after destination for guests looking for an unforgettable travel experience.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Richiedi un Preventivo' : 'Request a Quote'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Restyling; 